import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'
import logoImg from '@/assets/images/logo/logo.png'

interface NavItem {
  key: RouteKey
  labelKey: string
}

const navigation: NavItem[] = [
  { key: 'home', labelKey: 'nav.home' },
  { key: 'about', labelKey: 'nav.about' },
  { key: 'management', labelKey: 'nav.management' },
  { key: 'consulting', labelKey: 'nav.consulting' },
  { key: 'training', labelKey: 'nav.training' },
  { key: 'careers', labelKey: 'nav.careers' },
  { key: 'locations', labelKey: 'nav.locations' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

function NavLink({
  item,
  isScrolled,
  isActive
}: {
  item: NavItem
  isScrolled: boolean
  isActive: boolean
}) {
  const { t } = useTranslation('common')
  const [isHovered, setIsHovered] = useState(false)

  return (
    <LocalizedLink
      to={item.key}
      className={cn(
        'relative px-2 py-2 text-xs font-medium transition-colors duration-200 whitespace-nowrap',
        isScrolled
          ? isActive
            ? 'text-gray-900'
            : 'text-gray-600 hover:text-gray-900'
          : isActive
            ? 'text-white'
            : 'text-white/80 hover:text-white'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {t(item.labelKey)}
      {/* Vercel-style animated underline */}
      <motion.span
        className={cn(
          'absolute bottom-0 left-2 right-2 h-[2px] rounded-full',
          isScrolled ? 'bg-gray-900' : 'bg-white'
        )}
        initial={false}
        animate={{
          scaleX: isHovered || isActive ? 1 : 0,
          opacity: isHovered || isActive ? 1 : 0,
        }}
        transition={{
          duration: 0.2,
          ease: [0.4, 0, 0.2, 1],
        }}
        style={{ originX: 0.5 }}
      />
    </LocalizedLink>
  )
}

export function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const { t, i18n } = useTranslation('common')
  const lang = (i18n.language || 'it') as SupportedLanguage

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [location])

  // Check if current path matches a nav item
  const isActiveRoute = (key: RouteKey) => {
    const routePath = localizedRoutes[key][lang]
    const currentPath = location.pathname

    if (key === 'home') {
      return currentPath === `/${lang}` || currentPath === `/${lang}/`
    }
    return currentPath === `/${lang}/${routePath}` || currentPath === `/${lang}/${routePath}/`
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-fixed transition-all duration-300',
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      )}
    >
      <nav className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <LocalizedLink to="home" className="flex-shrink-0">
            <motion.img
              src={logoImg}
              alt="BF Wellness"
              className={cn(
                'h-14 w-auto transition-all duration-300',
                isScrolled && 'invert'
              )}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            />
          </LocalizedLink>

          {/* Desktop Navigation - All items on one line */}
          <div className="hidden lg:flex items-center">
            {navigation.map((item) => (
              <NavLink
                key={item.key}
                item={item}
                isScrolled={isScrolled}
                isActive={isActiveRoute(item.key)}
              />
            ))}
          </div>

          {/* Products Button + Language Selector */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Products Button - Now BEFORE language selector */}
            <motion.a
              href="https://www.zoecosmetics.it/it/"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200',
                isScrolled
                  ? 'bg-gray-900 text-white hover:bg-gray-800'
                  : 'bg-white text-gray-900 hover:bg-gray-100'
              )}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('nav.products')}
            </motion.a>

            {/* Language Selector */}
            <LanguageSelector isScrolled={isScrolled} />
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? t('aria.closeMenu') : t('aria.openMenu')}
              className={isScrolled ? 'text-gray-900' : 'text-white'}
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-6 w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-6 w-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              className="lg:hidden overflow-hidden"
            >
              <motion.div
                className="bg-white rounded-xl shadow-lg mt-2 p-4 border border-gray-100"
                initial={{ y: -10 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.2, delay: 0.1 }}
              >
                {/* Products Button - First in mobile too */}
                <motion.a
                  href="https://www.zoecosmetics.it/it/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-3 mb-2 text-base font-medium text-center bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200"
                  whileTap={{ scale: 0.98 }}
                >
                  {t('nav.products')}
                </motion.a>

                {/* Mobile Language Selector */}
                <LanguageSelector variant="mobile" />
                <div className="border-t border-gray-100 my-3" />

                {navigation.map((item, index) => (
                  <motion.div
                    key={item.key}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <LocalizedLink
                      to={item.key}
                      className={cn(
                        'block px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200',
                        isActiveRoute(item.key)
                          ? 'text-gray-900 bg-gray-50'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      )}
                    >
                      {t(item.labelKey)}
                    </LocalizedLink>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  )
}
